<script>
import EcButton from '../ui/EcButton.vue'
import { useMailV2Store } from '../../stores/mailV2'

function getTranslatable(val, locale) {
  if (!val) return ''
  if (typeof val === 'string') return val
  return val[locale] || val.fr || val.en || Object.values(val).find(function (v) { return v }) || ''
}

export default {
  name: 'MailV2TemplateList',
  components: { EcButton },
  props: {
    holderType: { type: String, required: true },
    holderId: { type: [String, Number], required: true },
    locale: { type: String, default: '' },
  },
  data: function () {
    return {
      store: useMailV2Store(),
      query: '',
      filterCategory: '',
      filterStatus: '',
    }
  },
  computed: {
    currentLocale: function () {
      return this.locale || 'fr'
    },
    filteredTemplates: function () {
      var self = this
      var list = this.store.templates || []

      if (this.filterCategory) {
        list = list.filter(function (item) { return item.category === self.filterCategory })
      }

      if (this.filterStatus === 'customized') {
        list = list.filter(function (item) { return item.is_customized })
      } else if (this.filterStatus === 'default') {
        list = list.filter(function (item) { return !item.is_customized })
      }

      if (this.query) {
        var q = this.query.toLowerCase()
        list = list.filter(function (item) {
          return getTranslatable(item.type_name, self.currentLocale).toLowerCase().indexOf(q) !== -1 ||
            (item.code && item.code.toLowerCase().indexOf(q) !== -1)
        })
      }

      return list
    },
  },
  watch: {
    holderId: function () {
      this.loadTemplates()
    },
  },
  mounted: function () {
    this.loadTemplates()
  },
  methods: {
    getTranslatable: getTranslatable,
    loadTemplates: function () {
      return this.store.listTemplates(this.holderType, this.holderId)
    },
  },
}
</script>

<template>
  <div class="ec-mv2-template-list">
    <!-- Filters -->
    <div class="ec-mv2-filters">
      <select v-model="filterCategory" class="ec-mv2-filter-select">
        <option value="">{{ $ecTranslate('MV2.ALL_CATEGORIES') }}</option>
        <option value="transactional">{{ $ecTranslate('MV2.TRANSACTIONAL') }}</option>
        <option value="notification">{{ $ecTranslate('MV2.NOTIFICATION') }}</option>
        <option value="marketing">{{ $ecTranslate('MV2.MARKETING') }}</option>
      </select>

      <select v-model="filterStatus" class="ec-mv2-filter-select">
        <option value="">{{ $ecTranslate('MV2.ALL_STATUS') }}</option>
        <option value="customized">{{ $ecTranslate('MV2.CUSTOMIZED') }}</option>
        <option value="default">{{ $ecTranslate('MV2.USING_DEFAULT') }}</option>
      </select>

      <div class="ec-mv2-search">
        <i class="bi bi-search" />
        <input
          v-model="query"
          type="search"
          :placeholder="$ecTranslate('ELIXIO_CONNECT.SEARCH')"
          class="ec-mv2-search-input"
        />
      </div>
    </div>

    <!-- Template cards -->
    <div v-if="store.loading" class="ec-mv2-loading">
      <ec-spinner />
    </div>

    <div v-else-if="filteredTemplates.length" class="ec-mv2-cards">
      <div
        v-for="item in filteredTemplates"
        :key="item.mail_type_id != null ? item.mail_type_id : item.id"
        class="ec-mv2-card"
      >
        <div class="ec-mv2-card-header">
          <div class="ec-mv2-card-info">
            <h5 class="ec-mv2-card-title">
              {{ getTranslatable(item.type_name, currentLocale) }}
            </h5>
            <span class="ec-mv2-card-code">{{ item.code }}</span>
          </div>
          <div class="ec-mv2-card-badges">
            <span class="ec-mv2-badge-category" :class="'ec-mv2-badge-category--' + item.category">
              {{ $ecTranslate('MV2.' + item.category.toUpperCase()) }}
            </span>
            <span
              class="ec-mv2-badge-status"
              :class="item.is_customized ? 'ec-mv2-badge-status--custom' : 'ec-mv2-badge-status--default'"
            >
              {{ item.is_customized ? $ecTranslate('MV2.CUSTOMIZED') : $ecTranslate('MV2.USING_DEFAULT') }}
            </span>
          </div>
        </div>

        <div class="ec-mv2-card-locales">
          <span
            v-for="loc in item.available_locales"
            :key="loc"
            class="ec-mv2-locale-chip"
          >
            {{ loc.toUpperCase() }}
          </span>
        </div>

        <div class="ec-mv2-card-actions">
          <template v-if="!item.is_customized">
            <EcButton variant="outline-primary" size="sm" icon="pencil" @click="$emit('customize', item)">
              {{ $ecTranslate('MV2.CUSTOMIZE') }}
            </EcButton>
          </template>
          <template v-else>
            <div style="display: contents">
              <EcButton variant="outline-primary" size="sm" icon="pencil" @click="$emit('edit', item)">
                {{ $ecTranslate('ELIXIO_CONNECT.EDIT') }}
              </EcButton>
              <EcButton variant="outline-danger" size="sm" icon="arrow-counterclockwise" @click="$emit('reset', item)">
                {{ $ecTranslate('MV2.RESET_DEFAULT') }}
              </EcButton>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div v-else class="ec-mv2-empty">
      {{ $ecTranslate('MV2.NO_TEMPLATES') }}
    </div>
  </div>
</template>

<style scoped>
.ec-mv2-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}
.ec-mv2-filter-select {
  padding: 8px 12px;
  border: 1px solid var(--ec-border-color, #dee2e6);
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  min-width: 180px;
}
.ec-mv2-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid var(--ec-border-color, #dee2e6);
  border-radius: 6px;
  background: #fff;
  flex: 1;
  min-width: 200px;
}
.ec-mv2-search-input {
  border: none;
  outline: none;
  width: 100%;
  font-size: 14px;
}
.ec-mv2-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ec-mv2-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  border: 1px solid var(--ec-border-color, #e9ecef);
  border-radius: 8px;
  background: #fff;
  transition: box-shadow 0.15s;
}
.ec-mv2-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.ec-mv2-card-header {
  flex: 1;
}
.ec-mv2-card-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ec-text-color, #333);
}
.ec-mv2-card-code {
  font-size: 11px;
  font-family: monospace;
  color: #999;
}
.ec-mv2-card-badges {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}
.ec-mv2-badge-category {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}
.ec-mv2-badge-category--transactional { background: #e3f2fd; color: #1565c0; }
.ec-mv2-badge-category--notification { background: #fff3e0; color: #e65100; }
.ec-mv2-badge-category--marketing { background: #f3e5f5; color: #7b1fa2; }
.ec-mv2-badge-status {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}
.ec-mv2-badge-status--custom { background: #e8f5e9; color: #2e7d32; }
.ec-mv2-badge-status--default { background: #f5f5f5; color: #757575; }
.ec-mv2-card-locales {
  display: flex;
  gap: 4px;
}
.ec-mv2-locale-chip {
  padding: 2px 8px;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #666;
}
.ec-mv2-card-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.ec-mv2-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  transition: all 0.15s;
}
.ec-mv2-btn--outline {
  color: var(--ec-primary-color, #0091ca);
  border-color: var(--ec-primary-color, #0091ca);
}
.ec-mv2-btn--outline:hover {
  background: var(--ec-primary-color, #0091ca);
  color: #fff;
}
.ec-mv2-btn--outline-danger {
  color: #dc3545;
  border-color: #dc3545;
}
.ec-mv2-btn--outline-danger:hover {
  background: #dc3545;
  color: #fff;
}
.ec-mv2-loading {
  display: flex;
  justify-content: center;
  padding: 40px;
}
.ec-mv2-empty {
  text-align: center;
  padding: 40px;
  color: #999;
}
</style>
